import type { LocationDescriptor, NavigationScreenState } from '@gitbook/app-navigation';
import { resolveLocationToNavigationScreen } from '@gitbook/app-navigation';

import {
    AgentsScreen,
    ChangeRequestsScreen,
    CollectionScreen,
    ContentGapsScreen,
    HomeScreen,
    IntegrationScreen,
    IntegrationsScreen,
    LinkAuditScreen,
    OpenAPISpecScreen,
    OpenAPISpecsHomeScreen,
    OrganizationContentScreen,
    SitesListScreen,
    StyleguideNewScreen,
    StyleguidesScreen,
    TrashScreen,
    TranslationsScreen,
    TriageScreen,
} from '.';
import type { Preloadable } from '../navigation';
import {
    ChangeRequestOverview,
    SiteAnalytics,
    SiteCustomizationScreen,
    SiteNewAnalytics,
    SiteNewInsights,
    SitePreviewScreen,
    SiteSettings,
    SpacePreview,
} from './screenBunches';
import { SiteScreen } from './Site';
import { SpaceScreen } from './Space';

/**
 * The single lazy screen for each screen type that only ever renders one
 * chunk, mirroring `MainScreenSwitch` in `Application.tsx`. Screen types
 * rendered outside `MainScreenSwitch` (auth flows, redirects, most settings, …)
 * are intentionally absent — they either aren't code-split or aren't worth
 * preloading at boot.
 */
const SINGLE_SCREEN_PRELOADABLES: Partial<Record<NavigationScreenState['type'], Preloadable[]>> = {
    home: [HomeScreen],
    sites: [SitesListScreen],
    collection: [CollectionScreen],
    trash: [TrashScreen],
    org_content: [OrganizationContentScreen],
    styleguides: [StyleguidesScreen],
    styleguide_new: [StyleguideNewScreen],
    agents: [AgentsScreen],
    change_requests: [ChangeRequestsScreen],
    triage: [TriageScreen],
    content_gaps: [ContentGapsScreen],
    link_audit: [LinkAuditScreen],
};

/**
 * Resolve the chunks the current route needs to render, mirroring the
 * `switch (screen.type)` in `MainScreenSwitch` and the mode/tab switches in
 * `SpaceScreen.tsx` and `SiteScreen.tsx`. Kept in sync manually — see the
 * breadcrumb comments at each of those switches.
 */
export function getPreloadablesForScreen(screen: NavigationScreenState): Preloadable[] {
    switch (screen.type) {
        case 'space': {
            const preloadables: Preloadable[] = [SpaceScreen];
            switch (screen.mode) {
                case 'preview':
                    preloadables.push(SpacePreview);
                    break;
                case 'overview':
                    preloadables.push(ChangeRequestOverview);
                    break;
                case 'edit':
                case 'diff':
                case 'checks':
                case undefined:
                    // These ship inside the SpaceScreen chunk itself.
                    break;
                default:
                    break;
            }
            return preloadables;
        }
        case 'site': {
            const preloadables: Preloadable[] = [SiteScreen];
            switch (screen.tab?.type) {
                case 'settings':
                    preloadables.push(SiteSettings);
                    break;
                case 'analytics':
                    // The flag isn't resolved here, so warm both variants of the screen.
                    preloadables.push(SiteAnalytics, SiteNewAnalytics);
                    break;
                case 'insights':
                    preloadables.push(SiteNewInsights);
                    break;
                case 'customization':
                    preloadables.push(SiteCustomizationScreen);
                    break;
                case 'preview':
                    preloadables.push(SitePreviewScreen);
                    break;
                default:
                    break;
            }
            return preloadables;
        }
        case 'org_settings':
            switch (screen.tab.type) {
                case 'integrations':
                    return [screen.tab.integration ? IntegrationScreen : IntegrationsScreen];
                case 'openapi':
                    return [screen.tab.spec ? OpenAPISpecScreen : OpenAPISpecsHomeScreen];
                case 'translations':
                    return [TranslationsScreen];
                default:
                    return [];
            }
        default:
            return SINGLE_SCREEN_PRELOADABLES[screen.type] ?? [];
    }
}

/**
 * Fire-and-forget preload of the chunks needed to render `location`, so they
 * download in parallel with boot-time auth/data requests instead of only
 * starting once React's render reaches them.
 */
export function preloadChunksForLocation(location: LocationDescriptor): void {
    try {
        const screen = resolveLocationToNavigationScreen(location);
        for (const preloadable of getPreloadablesForScreen(screen)) {
            void preloadable.preload().catch(() => {});
        }
    } catch {
        // Preloading is an optimization; never let it break boot.
    }
}
